import api from '../lib/axios';
import type { ApiResponse } from '@maios/shared';

// ============================================================================
// Types
// ============================================================================

export interface FigmaConnectionStatus {
  connected: boolean;
  figmaTeamId?: string;
  connectedBy?: { userId: string; name: string };
  connectedAt?: string;
  webhookActive?: boolean;
  fileCount: number;
}

export interface FigmaMappedFile {
  figmaFileId: string;
  fileKey: string;
  fileName: string;
  lastModified: string | null;
  thumbnailUrl: string | null;
}

export interface FigmaBrowseProject {
  id: string;
  name: string;
  files: {
    key: string;
    name: string;
    thumbnail_url: string;
    last_modified: string;
  }[];
}

// ============================================================================
// API Functions
// ============================================================================

export async function getConnectionStatus(projectId: string): Promise<FigmaConnectionStatus> {
  const res = await api.get<ApiResponse<FigmaConnectionStatus>>(
    `/projects/${projectId}/figma/connection`,
  );
  return res.data.data!;
}

export async function disconnectFigma(projectId: string): Promise<{ disconnected: boolean }> {
  const res = await api.delete<ApiResponse<{ disconnected: boolean }>>(
    `/projects/${projectId}/figma/connection`,
  );
  return res.data.data!;
}

export async function getMappedFiles(projectId: string): Promise<{ files: FigmaMappedFile[] }> {
  const res = await api.get<ApiResponse<{ files: FigmaMappedFile[] }>>(
    `/projects/${projectId}/figma/files`,
  );
  return res.data.data!;
}

export async function addFileMapping(
  projectId: string,
  fileKey: string,
  fileName: string,
): Promise<FigmaMappedFile> {
  const res = await api.post<ApiResponse<FigmaMappedFile>>(
    `/projects/${projectId}/figma/files`,
    { fileKey, fileName },
  );
  return res.data.data!;
}

export async function removeFileMapping(projectId: string, figmaFileId: string): Promise<void> {
  await api.delete(`/projects/${projectId}/figma/files/${figmaFileId}`);
}

export async function browseFigmaTeamFiles(
  projectId: string,
): Promise<{ projects: FigmaBrowseProject[] }> {
  const res = await api.get<ApiResponse<{ projects: FigmaBrowseProject[] }>>(
    `/projects/${projectId}/figma/browse`,
  );
  return res.data.data!;
}

export async function getAuthUrl(projectId: string): Promise<string> {
  const res = await api.get<ApiResponse<{ authUrl: string }>>(
    `/auth/figma`,
    { params: { projectId } },
  );
  return res.data.data!.authUrl;
}

export async function updateTeamId(
  projectId: string,
  figmaTeamId: string,
): Promise<{ figmaTeamId: string }> {
  const res = await api.patch<ApiResponse<{ figmaTeamId: string }>>(
    `/projects/${projectId}/figma/connection/team`,
    { figmaTeamId },
  );
  return res.data.data!;
}
